import { toast } from 'sonner'

export type BrowserNotificationPermission = NotificationPermission | 'unsupported'

export interface BrowserNotificationPayload {
  dedupeKey: string
  title: string
  body: string
  tag?: string
  fallbackMessage?: string
  onClick?: () => void
}

const DEDUPE_TTL_MS = 5_000

class NotificationService {
  private recentKeys = new Map<string, number>()

  isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window
  }

  getPermission(): BrowserNotificationPermission {
    if (!this.isSupported()) return 'unsupported'
    return window.Notification.permission
  }

  async requestPermission(): Promise<BrowserNotificationPermission> {
    if (!this.isSupported()) return 'unsupported'
    return window.Notification.requestPermission()
  }

  private prune(): void {
    const now = Date.now()
    for (const [key, expiresAt] of this.recentKeys.entries()) {
      if (expiresAt <= now) {
        this.recentKeys.delete(key)
      }
    }
  }

  private shouldDrop(key: string): boolean {
    this.prune()
    if (this.recentKeys.has(key)) return true
    this.recentKeys.set(key, Date.now() + DEDUPE_TTL_MS)
    return false
  }

  notify(payload: BrowserNotificationPayload): void {
    if (this.shouldDrop(payload.dedupeKey)) return

    const fallbackMessage = payload.fallbackMessage ?? `${payload.title}: ${payload.body}`
    const permission = this.getPermission()

    if (permission !== 'granted') {
      toast.info(fallbackMessage)
      return
    }

    const notification = new window.Notification(payload.title, {
      body: payload.body,
      tag: payload.tag ?? payload.dedupeKey,
    })
    notification.onclick = () => {
      window.focus()
      payload.onClick?.()
      notification.close()
    }
  }
}

export const notificationService = new NotificationService()
